import { useMemo } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"
import { DotText } from "@/v2-backoffice-client/components/dot"
import CustomerTenantRemoveManyButton from "@/v2-backoffice-client/customer/tenant/remove/many/CustomerTenantRemoveManyButton"
import CustomerTenantTableRowToolbar from "@/v2-backoffice-client/customer/tenant/CustomerTenantTableRowToolbar"
import CustomerTenantAddSelectorDialogButton from "@/v2-backoffice-client/customer/tenant/add/CustomerTenantAddSelectorDialogButton"
import { buildScopeUsageColumns } from "@/v2-backoffice-client/usage/usage.columns"

const ACTIVE_STATUS_CODE = 0
const DELETED_STATUS_CODE = 1

function createCustomerTenantColumns() {
  return createColumns<TenantFragment>({
    enableSelection: true,
    enableSorting: true,
    enableHiding: true,
    columns: {
      Name: {
        id: "Name",
        label: "Name",
        to: ({ row }) => `>>?v=tenant:${row.original.Id}`,
      },
      UserCount: { id: "UserCount", label: "Total Users", defaultSize: 70 },
      ActiveUserCount: {
        id: "ActiveUserCount",
        label: "Active Users",
        defaultSize: 80,
      },
      InactiveUserCount: {
        id: "InactiveUserCount",
        label: "Inactive Users",
        defaultSize: 80,
      },
      // Same usage aggregates as the top-level Teams board; the deep link
      // stacks the team panel open on its Activity tab.
      ...buildScopeUsageColumns<TenantFragment>({
        buildTo: (row) => `>>?v=tenant:${row.Id}&tenant-view-tabs-tab=sign-ins`,
      }),
      InvitationCount: {
        id: "InvitationCount",
        label: "Invitations",
        defaultHidden: true,
        defaultSize: 80,
      },
      IsDeleted: {
        id: "IsDeleted",
        label: "Deleted",
        defaultSize: 70,
        defaultHidden: true,
        enableGrouping: true,
        renderCell({ row }) {
          return (
            <DotText
              variant={row.original.IsDeleted ? "destructive" : "success-1"}
            >
              {row.original.IsDeleted ? "Yes" : "No"}
            </DotText>
          )
        },
        exportValue({ row }) {
          return row.original.IsDeleted ? "Yes" : "No"
        },
      },
      Id: { id: "Id", label: "Id", defaultSize: 50, defaultHidden: true },
    },
  })
}

export type CustomerTenantTableProps = Omit<
  TableProps<TenantFragment>,
  "data" | "columns"
> & {
  customerName: string
  customerId: number
  externalCustomerId?: string
  tenants: TenantFragment[]
  isLoading?: boolean
  error?: Error
  activeCount?: number
  removedCount?: number
  onDeletedFilterChange?(onlyDeleted: boolean | undefined): void
  onRefresh?(): void
}

function CustomerTenantTable(props: CustomerTenantTableProps) {
  const {
    className,
    customerName,
    customerId,
    externalCustomerId,
    tenants,
    isLoading,
    error,
    activeCount,
    removedCount,
    onDeletedFilterChange,
    onRefresh,
    ...tableProps
  } = props

  const columns = useMemo(() => createCustomerTenantColumns(), [])

  return (
    <Table
      className={cn("customer-tenant-table", className)}
      tableId="customer-tenant-table"
      data={tenants}
      columns={columns}
      enableRowSelection
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.StatusFilter
          defaultValue={ACTIVE_STATUS_CODE}
          onChange={(value) => {
            onDeletedFilterChange?.(
              value === null ? undefined : value === DELETED_STATUS_CODE,
            )
          }}
        >
          <Table.StatusFilter.Option
            value={null}
            count={
              activeCount !== undefined && removedCount !== undefined
                ? activeCount + removedCount
                : undefined
            }
          >
            All
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            variant="success"
            value={ACTIVE_STATUS_CODE}
            count={activeCount}
          >
            Active
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            variant="destructive"
            value={DELETED_STATUS_CODE}
            count={removedCount}
          >
            Removed
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />
        <Table.Search column="Name" placeholder="Search by name..." />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu
          onRefresh={onRefresh}
          exportFileName="customer-tenants"
        >
          {externalCustomerId && (
            <CustomerTenantAddSelectorDialogButton
              externalCustomerId={externalCustomerId}
              customerName={customerName}
              existingTenantIds={tenants.map((t) => t.Id)}
            />
          )}
        </Table.ToolbarMenu>
      </Table.Toolbar>

      <Table.FilterList />

      <Table.RowToolbar<TenantFragment> column="Name">
        {(tenant) => (
          <CustomerTenantTableRowToolbar
            customerId={customerId}
            customerName={customerName}
            tenant={tenant}
          />
        )}
      </Table.RowToolbar>

      <Table.Content isLoading={isLoading} error={error} />

      <Table.SelectionToolbar<TenantFragment>>
        {(tenants, reset) => (
          <>
            <Table.ExportSelectionButton
              fileName="customer-tenants"
              selectionCount={tenants.length}
            />
            <CustomerTenantRemoveManyButton
              variant="destructive"
              isStacked
              iconSrc="trash"
              content="Remove selected teams from customer"
              customerName={customerName}
              customerId={customerId}
              tenantIds={tenants.map((t) => t.Id)}
              onCompleted={reset}
            >
              Remove
            </CustomerTenantRemoveManyButton>
          </>
        )}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default CustomerTenantTable
